import React, { FC } from 'react';
import Lozenge from '@atlaskit/lozenge';
import Tooltip from '@atlaskit/tooltip';
import { css } from '@emotion/core';
import { LowTrafficStatusDTO, SloType } from '@atlassiansox/phobos-client-ts';
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line';
import isNil from 'lodash/isNil';

import { flexCenter, SPACE_COZY, spaceGapMinimum, textTransformLowercase } from '@/styles';
import SfxIcon from '@/components/primitive/sfx-icon';
import SloMenu from '@/components/primitive/slo-menu';
import { useLocationController } from '@/controllers';

import { SLINumberProps } from '../common/utils';
import SloTypeLozenge from '../../slo-card/lozenges/slo-type-lozenge';
import { getIndicatorCohortMemberTagName } from '../../slo-group/utils';
import { SLOIndicatorWithGroupType } from '../../slo-group/types';

const rootStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const innerLeftStyles = css`
  display: flex;
  align-items: center;
  gap: ${SPACE_COZY}px;
`;

const headerGroupMemberTagStyles = css`
  ${textTransformLowercase};
  font-size: 12px;
`;

type SloPageHeaderProps = {
  publicId: string;
  name: string;
  type: SloType;
  indicator?: SLOIndicatorWithGroupType;
  isDraft: boolean;
  isAlertable?: boolean;
  isSkeleton?: boolean;
  parentCapabilityPublicId: string;
  lowTrafficStatus?: LowTrafficStatusDTO;
} & SLINumberProps;

const SloPageHeader: FC<SloPageHeaderProps> = ({
  publicId,
  name,
  type,
  indicator,
  isDraft,
  isAlertable,
  isSkeleton,
  parentCapabilityPublicId,
  lowTrafficStatus,
}) => {
  const { isSLOGroupPage, isSLOGroupMemberPage } = useLocationController();
  return (
    <div css={[rootStyles]} data-testid="slo-page-header">
      <div css={[innerLeftStyles]}>
        {isSLOGroupPage && <Lozenge appearance="inprogress">slo group</Lozenge>}
        {isSLOGroupMemberPage && (
          <Lozenge appearance="inprogress">
            <span css={headerGroupMemberTagStyles}>
              {getIndicatorCohortMemberTagName(indicator)}
            </span>
          </Lozenge>
        )}
        {isDraft && <Lozenge appearance="moved">🛠 Draft</Lozenge>}

        <SloTypeLozenge type={type} />

        {!isSLOGroupPage &&
          lowTrafficStatus &&
          lowTrafficStatus.inLowTrafficState &&
          !isNil(lowTrafficStatus.currentTraffic) &&
          lowTrafficStatus.currentTraffic > 0 && (
            <Lozenge appearance="new">
              <div css={[flexCenter, spaceGapMinimum]}>
                <GraphLineIcon label="Low traffic" size="small" />
                low traffic
              </div>
            </Lozenge>
          )}
      </div>
      <div
        css={[
          flexCenter,
          css`
            gap: 1em;
          `,
        ]}
      >
        {!isDraft && isAlertable && (
          <Tooltip
            content="This SLO will raise alerts via SignalFx.
            When this SLO experiences a breach or burn event,
            it will page the owning team via OpsGenie"
            position="left"
          >
            <SfxIcon />
          </Tooltip>
        )}
        <SloMenu
          publicId={publicId}
          isSkeleton={isSkeleton}
          name={name}
          indicator={indicator}
          parentCapabilityPublicId={parentCapabilityPublicId}
        />
      </div>
    </div>
  );
};

export default SloPageHeader;
